// filepath: c:\Users\2396375\assenivesh-ui\an-vue\src\components\dashboard\FinancialInformationWizard.vue
<script setup>
import Dialog from 'primevue/dialog';
import { ref, computed } from 'vue';
import { useUserStore } from '@/store/user-store';
import AnButton from '../common/AnButton.vue';

const props = defineProps({ modelValue: Boolean });
const emit = defineEmits(['update:modelValue']);
const store = useUserStore();

// Wizard state
const currentStep = ref(1);
const totalSteps = 8;
const error = ref('');
const isSubmitting = ref(false);

// Step 1: Household Income
const householdIncome = ref({
  salary: '',
  selfEmployed: '',
  rental: '',
  sideHustle: '',
  other: ''
});

// Step 2: Employment Type
const employmentType = ref('Private');
const employmentTypes = ['Government', 'Private', 'Corporate', 'Self-Employed'];

// Step 3: Household Expenses
const householdExpenses = ref({
  rent: '',
  groceries: '',
  utilities: '',
  transportation: '',
  maid: '',
  holidays: '',
  other: ''
});

// Step 4: EMI Outflows (Detailed Breakup)
const emiOutflows = ref({
  home: { amount: '', interestRate: 0 },
  vehicle: { amount: '', interestRate: 0 },
  personal: { amount: '', interestRate: 0 },
  creditCard: { amount: '', interestRate: 0 },
  business: { amount: '', interestRate: 0 }
});

// Step 5: Insurance Premiums
const insurancePremiums = ref({
  health: { premium: '', coverage: '' },
  term: { premium: '', coverage: '' }
});

// Step 6: Emergency Fund
const emergencyFund = ref('');

// Step 7: Average Savings & SIPs
const savingsInfo = ref({
  monthlyLiquidSavings: '',
  monthlySIP: '',
  weightedAvgSIPReturn: ''
});

// Step 8: Review & Confirmation
const showReview = computed(() => currentStep.value === totalSteps);

// Computed total values for review
const totalIncome = computed(() => {
  return Object.values(householdIncome.value)
    .filter(v => v !== '')
    .reduce((sum, val) => sum + parseFloat(val || 0), 0);
});

const totalExpenses = computed(() => {
  return Object.values(householdExpenses.value)
    .filter(v => v !== '')
    .reduce((sum, val) => sum + parseFloat(val || 0), 0);
});

const totalEMI = computed(() => {
  return Object.values(emiOutflows.value)
    .filter(emi => emi.amount !== '')
    .reduce((sum, emi) => sum + parseFloat(emi.amount || 0), 0);
});

const totalInsurance = computed(() => {
  let total = 0;
  if (insurancePremiums.value.health.premium) total += parseFloat(insurancePremiums.value.health.premium);
  if (insurancePremiums.value.term.premium) total += parseFloat(insurancePremiums.value.term.premium);
  return total;
});

const totalSavings = computed(() => {
  let total = 0;
  if (savingsInfo.value.monthlyLiquidSavings) total += parseFloat(savingsInfo.value.monthlyLiquidSavings);
  if (savingsInfo.value.monthlySIP) total += parseFloat(savingsInfo.value.monthlySIP);
  return total;
});

const closeDialog = () => {
  emit('update:modelValue', false);
  // Reset wizard state after a delay
  setTimeout(() => {
    currentStep.value = 1;
    error.value = '';
  }, 300);
};

const nextStep = () => {
  if (currentStep.value < totalSteps) {
    currentStep.value++;
    error.value = '';
  }
};

const previousStep = () => {
  if (currentStep.value > 1) {
    currentStep.value--;
    error.value = '';
  }
};

const formatCurrency = (value) => {
  if (!value) return '₹0';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(value);
};

const handleSubmit = async () => {
  isSubmitting.value = true;
  error.value = '';

  try {
    // Prepare records array for updateFinancialStatement API
    const records = [];

    // 1. Income records
    if (householdIncome.value.salary) {
      records.push({
        itemId: null,
        incomeHead: 'Earned Income',
        incomeType: 'Salary',
        incomeDate: new Date().toISOString().slice(0, 10),
        incomeAmount: parseFloat(householdIncome.value.salary)
      });
    }
    if (householdIncome.value.selfEmployed) {
      records.push({
        itemId: null,
        incomeHead: 'Earned Income',
        incomeType: 'Self Employed',
        incomeDate: new Date().toISOString().slice(0, 10),
        incomeAmount: parseFloat(householdIncome.value.selfEmployed)
      });
    }
    if (householdIncome.value.rental) {
      records.push({
        itemId: null,
        incomeHead: 'Passive Income',
        incomeType: 'Rental Income',
        incomeDate: new Date().toISOString().slice(0, 10),
        incomeAmount: parseFloat(householdIncome.value.rental)
      });
    }
    if (householdIncome.value.sideHustle) {
      records.push({
        itemId: null,
        incomeHead: 'Earned Income',
        incomeType: 'Side Hustle/Business',
        incomeDate: new Date().toISOString().slice(0, 10),
        incomeAmount: parseFloat(householdIncome.value.sideHustle)
      });
    }

    // 2. Expense records
    const expenseMapping = {
      rent: { head: 'Necessary Spending', type: 'Housing & Rent' },
      groceries: { head: 'Necessary Spending', type: 'Groceries & Household Supplies' },
      utilities: { head: 'Necessary Spending', type: 'Utilities' },
      transportation: { head: 'Necessary Spending', type: 'Transportation' },
      maid: { head: 'Necessary Spending', type: 'Domestic Help' },
      holidays: { head: 'Discretionary Spending', type: 'Entertainment & Leisure' },
      other: { head: 'Discretionary Spending', type: 'Other Expenses' }
    };

    Object.entries(householdExpenses.value).forEach(([key, value]) => {
      if (value) {
        const mapping = expenseMapping[key];
        records.push({
          itemId: null,
          expenseHead: mapping.head,
          expenseType: mapping.type,
          expenseDate: new Date().toISOString().slice(0, 10),
          expenseAmount: parseFloat(value)
        });
      }
    });

    // 3. Liability (EMI) records
    const liabilityMapping = {
      home: 'Home Loan',
      vehicle: 'Car Loan',
      personal: 'Personal Loan',
      creditCard: 'Credit Card',
      business: 'Personal Loan' // Map business to Personal Loan
    };

    Object.entries(emiOutflows.value).forEach(([key, emi]) => {
      if (emi.amount) {
        records.push({
          itemId: null,
          liabilityName: liabilityMapping[key],
          loanInterestRate: parseFloat(emi.interestRate || 0),
          loanEMIAmount: parseFloat(emi.amount),
          liabilityDescription: `${key.charAt(0).toUpperCase() + key.slice(1)} EMI`,
          liabilityDate: new Date().toISOString().slice(0, 10),
          amountOutstanding: parseFloat(emi.amount) * 12 // Estimate: 1 year outstanding
        });
      }
    });

    // Prepare additional financial data (now supported by backend)
    // Note: Unpopulated fields are sent as 0 instead of null for database consistency
    const additionalData = {
      employmentType: employmentType.value,
      emergencyFund: emergencyFund.value ? parseFloat(emergencyFund.value) : 0,
      monthlyLiquidSavings: savingsInfo.value.monthlyLiquidSavings ? parseFloat(savingsInfo.value.monthlyLiquidSavings) : 0,
      monthlySIP: savingsInfo.value.monthlySIP ? parseFloat(savingsInfo.value.monthlySIP) : 0,
      weightedAvgSIPReturn: savingsInfo.value.weightedAvgSIPReturn ? parseFloat(savingsInfo.value.weightedAvgSIPReturn) : 0
    };

    // Add insurance data if provided
    const insuranceData = [];
    if (insurancePremiums.value.health.premium) {
      insuranceData.push({
        insuranceType: 'Health',
        premium: parseFloat(insurancePremiums.value.health.premium),
        coverage: insurancePremiums.value.health.coverage ? parseFloat(insurancePremiums.value.health.coverage) : null,
        policyDate: new Date().toISOString().slice(0, 10)
      });
    }
    if (insurancePremiums.value.term.premium) {
      insuranceData.push({
        insuranceType: 'Term',
        premium: parseFloat(insurancePremiums.value.term.premium),
        coverage: insurancePremiums.value.term.coverage ? parseFloat(insurancePremiums.value.term.coverage) : null,
        policyDate: new Date().toISOString().slice(0, 10)
      });
    }

    // Add insurance to additional data if exists
    if (insuranceData.length > 0) {
      additionalData.insurance = insuranceData;
    }

    // Call API with all data
    const payload = {
      records,
      ...additionalData
    };

    console.log('Submitting financial data with additional fields:', payload);
    await store.updateFinancialStatement(payload);

    // Reload financial statement to get updated data (same as BalanceSheet.vue pattern)
    // Note: We only reload financial statement, NOT full dashboard data to avoid navigation
    await store.loadFinancialStatement(store.dashboardData.customerId);
    console.log('Financial statement reloaded after wizard submission');
    console.log('Updated financial statement:', store.financialStatement);

    store.fireAlert('Success', 'Financial information saved successfully!');

    // Small delay to ensure reactive updates propagate before closing
    await new Promise(resolve => setTimeout(resolve, 100));
    closeDialog();

  } catch (err) {
    error.value = 'Failed to save financial information. Please try again.';
    console.error('Submit error:', err);
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <Dialog
    modal
    :visible="modelValue"
    :closable="!isSubmitting"
    :dismissableMask="!isSubmitting"
    @update:visible="closeDialog"
    class="financial-wizard-dialog"
    :style="{ width: '800px' }"
  >
    <template #header>
      <div class="wizard-header">
        <h2 class="wizard-title"> &nbsp; Complete Financial Profile</h2>
        <div class="wizard-progress">
          <span class="progress-text">&nbsp; Step {{ currentStep }} of {{ totalSteps }}</span>
          <div class="progress-bar">
            <div class="progress-fill" :style="{ width: (currentStep / totalSteps * 100) + '%' }"></div>
          </div>
        </div>
      </div>
    </template>

    <div class="wizard-content">
      <!-- Step 1: Household Income -->
      <div v-if="currentStep === 1" class="wizard-step">
        <h3 class="step-title">
          <i class="pi pi-wallet"></i>
          What is your overall household income per month?
        </h3>
        <p class="step-description">Include all sources of income for accurate financial planning</p>

        <div class="form-grid">
          <div class="form-field">
            <label>Salary/Wages</label>
            <input v-model="householdIncome.salary" type="number" placeholder="₹" class="form-input" />
          </div>
          <div class="form-field">
            <label>Self-Employed Income</label>
            <input v-model="householdIncome.selfEmployed" type="number" placeholder="₹" class="form-input" />
          </div>
          <div class="form-field">
            <label>Rental Income</label>
            <input v-model="householdIncome.rental" type="number" placeholder="₹" class="form-input" />
          </div>
          <div class="form-field">
            <label>Side Hustle/Business</label>
            <input v-model="householdIncome.sideHustle" type="number" placeholder="₹" class="form-input" />
          </div>
        </div>

        <div class="summary-box">
          <span class="summary-label">Total Monthly Income:</span>
          <span class="summary-value">{{ formatCurrency(totalIncome) }}</span>
        </div>
      </div>

      <!-- Step 2: Employment Type -->
      <div v-if="currentStep === 2" class="wizard-step">
        <h3 class="step-title">
          <i class="pi pi-briefcase"></i>
          What is your employment type?
        </h3>
        <p class="step-description">This helps us understand your income stability and tax planning needs</p>

        <div class="employment-grid">
          <button
            v-for="type in employmentTypes"
            :key="type"
            :class="['employment-card', { active: employmentType === type }]"
            @click="employmentType = type"
          >
            <i :class="[
              'pi',
              type === 'Government' ? 'pi-building' :
              type === 'Private' ? 'pi-id-card' :
              type === 'Corporate' ? 'pi-briefcase' :
              'pi-user'
            ]"></i>
            <span>{{ type }}</span>
          </button>
        </div>
      </div>

      <!-- Step 3: Household Expenses -->
      <div v-if="currentStep === 3" class="wizard-step">
        <h3 class="step-title">
          <i class="pi pi-shopping-cart"></i>
          What are your monthly household expenses?
        </h3>
        <p class="step-description">Include all regular outflows like rent, groceries, utilities, etc.</p>

        <div class="form-grid">
          <div class="form-field">
            <label>Rent/Mortgage</label>
            <input v-model="householdExpenses.rent" type="number" placeholder="₹" class="form-input" />
          </div>
          <div class="form-field">
            <label>Groceries & Supplies</label>
            <input v-model="householdExpenses.groceries" type="number" placeholder="₹" class="form-input" />
          </div>
          <div class="form-field">
            <label>Utilities (electricity, water, etc.)</label>
            <input v-model="householdExpenses.utilities" type="number" placeholder="₹" class="form-input" />
          </div>
          <div class="form-field">
            <label>Transportation</label>
            <input v-model="householdExpenses.transportation" type="number" placeholder="₹" class="form-input" />
          </div>
          <div class="form-field">
            <label>Maid/Domestic Help</label>
            <input v-model="householdExpenses.maid" type="number" placeholder="₹" class="form-input" />
          </div>
          <div class="form-field">
            <label>Holidays (Monthly Average)</label>
            <input v-model="householdExpenses.holidays" type="number" placeholder="₹" class="form-input" />
          </div>
          <div class="form-field">
            <label>Other Expenses</label>
            <input v-model="householdExpenses.other" type="number" placeholder="₹" class="form-input" />
          </div>
        </div>

        <div class="summary-box">
          <span class="summary-label">Total Monthly Expenses:</span>
          <span class="summary-value">{{ formatCurrency(totalExpenses) }}</span>
        </div>
      </div>

      <!-- Step 4: EMI Outflows -->
      <div v-if="currentStep === 4" class="wizard-step">
        <h3 class="step-title">
          <i class="pi pi-credit-card"></i>
          What are your monthly EMI outflows?
        </h3>
        <p class="step-description">Break down your loans by type for accurate debt planning</p>

        <div class="emi-grid">
          <div class="emi-card">
            <h4><i class="pi pi-home"></i> Home Loan</h4>
            <div class="emi-inputs">
              <div class="form-field">
                <label>Monthly EMI</label>
                <input v-model="emiOutflows.home.amount" type="number" placeholder="₹" class="form-input" />
              </div>
              <div class="form-field">
                <label>Interest Rate (%)</label>
                <input v-model="emiOutflows.home.interestRate" type="number" step="0.1" placeholder="%" class="form-input" />
              </div>
            </div>
          </div>

          <div class="emi-card">
            <h4><i class="pi pi-car"></i> Vehicle Loan</h4>
            <div class="emi-inputs">
              <div class="form-field">
                <label>Monthly EMI</label>
                <input v-model="emiOutflows.vehicle.amount" type="number" placeholder="₹" class="form-input" />
              </div>
              <div class="form-field">
                <label>Interest Rate (%)</label>
                <input v-model="emiOutflows.vehicle.interestRate" type="number" step="0.1" placeholder="%" class="form-input" />
              </div>
            </div>
          </div>

          <div class="emi-card">
            <h4><i class="pi pi-wallet"></i> Personal Loan</h4>
            <div class="emi-inputs">
              <div class="form-field">
                <label>Monthly EMI</label>
                <input v-model="emiOutflows.personal.amount" type="number" placeholder="₹" class="form-input" />
              </div>
              <div class="form-field">
                <label>Interest Rate (%)</label>
                <input v-model="emiOutflows.personal.interestRate" type="number" step="0.1" placeholder="%" class="form-input" />
              </div>
            </div>
          </div>

          <div class="emi-card">
            <h4><i class="pi pi-credit-card"></i> Credit Card</h4>
            <div class="emi-inputs">
              <div class="form-field">
                <label>Monthly Payment</label>
                <input v-model="emiOutflows.creditCard.amount" type="number" placeholder="₹" class="form-input" />
              </div>
              <div class="form-field">
                <label>Interest Rate (%)</label>
                <input v-model="emiOutflows.creditCard.interestRate" type="number" step="0.1" placeholder="%" class="form-input" />
              </div>
            </div>
          </div>

          <div class="emi-card">
            <h4><i class="pi pi-briefcase"></i> Business Loan</h4>
            <div class="emi-inputs">
              <div class="form-field">
                <label>Monthly EMI</label>
                <input v-model="emiOutflows.business.amount" type="number" placeholder="₹" class="form-input" />
              </div>
              <div class="form-field">
                <label>Interest Rate (%)</label>
                <input v-model="emiOutflows.business.interestRate" type="number" step="0.1" placeholder="%" class="form-input" />
              </div>
            </div>
          </div>
        </div>

        <div class="summary-box">
          <span class="summary-label">Total Monthly EMI:</span>
          <span class="summary-value">{{ formatCurrency(totalEMI) }}</span>
        </div>
      </div>

      <!-- Step 5: Insurance Premiums -->
      <div v-if="currentStep === 5" class="wizard-step">
        <h3 class="step-title">
          <i class="pi pi-shield"></i>
          What are your insurance premiums?
        </h3>
        <p class="step-description">Insurance coverage is crucial for financial security</p>

        <div class="insurance-grid">
          <div class="insurance-card">
            <h4><i class="pi pi-heart"></i> Health Insurance</h4>
            <div class="form-field">
              <label>Annual Premium</label>
              <input v-model="insurancePremiums.health.premium" type="number" placeholder="₹" class="form-input" />
            </div>
            <div class="form-field">
              <label>Coverage Amount</label>
              <input v-model="insurancePremiums.health.coverage" type="number" placeholder="₹" class="form-input" />
            </div>
          </div>

          <div class="insurance-card">
            <h4><i class="pi pi-user"></i> Term Insurance</h4>
            <div class="form-field">
              <label>Annual Premium</label>
              <input v-model="insurancePremiums.term.premium" type="number" placeholder="₹" class="form-input" />
            </div>
            <div class="form-field">
              <label>Coverage Amount</label>
              <input v-model="insurancePremiums.term.coverage" type="number" placeholder="₹" class="form-input" />
            </div>
          </div>
        </div>

        <div class="summary-box">
          <span class="summary-label">Total Annual Insurance Premium:</span>
          <span class="summary-value">{{ formatCurrency(totalInsurance) }}</span>
        </div>
      </div>

      <!-- Step 6: Emergency Fund -->
      <div v-if="currentStep === 6" class="wizard-step">
        <h3 class="step-title">
          <i class="pi pi-money-bill"></i>
          What is your emergency fund status?
        </h3>
        <p class="step-description">Liquid cash maintained for unforeseen expenses (recommended: 6 months of expenses). Leave blank if you don't have an emergency fund yet.</p>

        <div class="emergency-fund-input">
          <label>Emergency Fund Amount</label>
          <input v-model="emergencyFund" type="number" placeholder="₹ (Enter 0 if none)" class="form-input-large" />

          <div v-if="emergencyFund && totalExpenses > 0" class="emergency-fund-status">
            <div class="status-indicator">
              <i :class="[
                'pi',
                (emergencyFund / totalExpenses) >= 6 ? 'pi-check-circle' : 'pi-exclamation-triangle'
              ]"></i>
              <span>
                {{ Math.round(emergencyFund / totalExpenses) }} months of expenses covered
              </span>
            </div>
            <p class="status-recommendation">
              {{ (emergencyFund / totalExpenses) >= 6
                ? '✓ Great! You have adequate emergency coverage'
                : `Consider building up to ₹${formatCurrency(totalExpenses * 6).replace('₹', '')} for 6 months coverage` }}
            </p>
          </div>
        </div>
      </div>

      <!-- Step 7: Savings & SIPs -->
      <div v-if="currentStep === 7" class="wizard-step">
        <h3 class="step-title">
          <i class="pi pi-chart-line"></i>
          What are your average monthly savings?
        </h3>
        <p class="step-description">Understanding your savings pattern helps optimize investment strategies. Leave blank or enter 0 if you don't have regular savings yet.</p>

        <div class="savings-grid">
          <div class="form-field-large">
            <label>
              <i class="pi pi-wallet"></i>
              Monthly Liquid Savings
            </label>
            <p class="field-hint">Cash/bank balance accumulated monthly (excluding SIPs). Enter 0 if none.</p>
            <input v-model="savingsInfo.monthlyLiquidSavings" type="number" placeholder="₹ (Enter 0 if none)" class="form-input" />
          </div>

          <div class="form-field-large">
            <label>
              <i class="pi pi-refresh"></i>
              Total Monthly SIP Investments
            </label>
            <p class="field-hint">All SIPs including Mutual Funds, LIC, PF, etc. Enter 0 if none.</p>
            <input v-model="savingsInfo.monthlySIP" type="number" placeholder="₹ (Enter 0 if none)" class="form-input" />
          </div>

          <div class="form-field-large">
            <label>
              <i class="pi pi-percentage"></i>
              Weighted Avg. Return on SIPs
            </label>
            <p class="field-hint">Average expected annual return across all your SIPs (%). Enter 0 if unknown.</p>
            <input v-model="savingsInfo.weightedAvgSIPReturn" type="number" step="0.1" placeholder="% (Enter 0 if unknown)" class="form-input" />
          </div>
        </div>

        <div class="summary-box">
          <span class="summary-label">Total Monthly Savings:</span>
          <span class="summary-value">{{ formatCurrency(totalSavings) }}</span>
        </div>

        <div v-if="totalIncome > 0 && totalSavings > 0" class="savings-analysis">
          <div class="analysis-card">
            <h4>Savings Rate</h4>
            <div class="analysis-value">{{ Math.round((totalSavings / totalIncome) * 100) }}%</div>
            <p class="analysis-hint">of your income is being saved</p>
          </div>
          <div class="analysis-card">
            <h4>Unutilized Cash</h4>
            <div class="analysis-value">{{ formatCurrency(parseFloat(savingsInfo.monthlyLiquidSavings || 0)) }}</div>
            <p class="analysis-hint">can be optimized for better returns</p>
          </div>
        </div>
      </div>

      <!-- Step 8: Review & Confirmation -->
      <div v-if="currentStep === 8" class="wizard-step review-step">
        <h3 class="step-title">
          <i class="pi pi-check-circle"></i>
          Review Your Financial Information
        </h3>
        <p class="step-description">Please verify all details before submitting</p>

        <div class="review-sections">
          <!-- Income Summary -->
          <div class="review-section">
            <h4><i class="pi pi-wallet"></i> Monthly Income</h4>
            <div class="review-items">
              <div v-if="householdIncome.salary" class="review-item">
                <span>Salary/Wages</span>
                <span class="value">{{ formatCurrency(householdIncome.salary) }}</span>
              </div>
              <div v-if="householdIncome.selfEmployed" class="review-item">
                <span>Self-Employed</span>
                <span class="value">{{ formatCurrency(householdIncome.selfEmployed) }}</span>
              </div>
              <div v-if="householdIncome.rental" class="review-item">
                <span>Rental Income</span>
                <span class="value">{{ formatCurrency(householdIncome.rental) }}</span>
              </div>
              <div class="review-item total">
                <span>Total Income</span>
                <span class="value">{{ formatCurrency(totalIncome) }}</span>
              </div>
            </div>
          </div>

          <!-- Employment -->
          <div class="review-section">
            <h4><i class="pi pi-briefcase"></i> Employment</h4>
            <div class="review-items">
              <div class="review-item">
                <span>Type</span>
                <span class="value">{{ employmentType }}</span>
              </div>
            </div>
          </div>

          <!-- Expenses Summary -->
          <div class="review-section">
            <h4><i class="pi pi-shopping-cart"></i> Monthly Expenses</h4>
            <div class="review-items">
              <div v-if="householdExpenses.rent" class="review-item">
                <span>Rent/Mortgage</span>
                <span class="value">{{ formatCurrency(householdExpenses.rent) }}</span>
              </div>
              <div v-if="householdExpenses.groceries" class="review-item">
                <span>Groceries</span>
                <span class="value">{{ formatCurrency(householdExpenses.groceries) }}</span>
              </div>
              <div class="review-item total">
                <span>Total Expenses</span>
                <span class="value">{{ formatCurrency(totalExpenses) }}</span>
              </div>
            </div>
          </div>

          <!-- EMI Summary -->
          <div class="review-section">
            <h4><i class="pi pi-credit-card"></i> Monthly EMI</h4>
            <div class="review-items">
              <div v-if="emiOutflows.home.amount" class="review-item">
                <span>Home Loan</span>
                <span class="value">{{ formatCurrency(emiOutflows.home.amount) }} @ {{ emiOutflows.home.interestRate }}%</span>
              </div>
              <div v-if="emiOutflows.vehicle.amount" class="review-item">
                <span>Vehicle Loan</span>
                <span class="value">{{ formatCurrency(emiOutflows.vehicle.amount) }} @ {{ emiOutflows.vehicle.interestRate }}%</span>
              </div>
              <div class="review-item total">
                <span>Total EMI</span>
                <span class="value">{{ formatCurrency(totalEMI) }}</span>
              </div>
            </div>
          </div>

          <!-- Insurance Summary -->
          <div class="review-section">
            <h4><i class="pi pi-shield"></i> Insurance</h4>
            <div class="review-items">
              <div v-if="insurancePremiums.health.premium" class="review-item">
                <span>Health ({{ formatCurrency(insurancePremiums.health.coverage) }} coverage)</span>
                <span class="value">{{ formatCurrency(insurancePremiums.health.premium) }}/year</span>
              </div>
              <div v-if="insurancePremiums.term.premium" class="review-item">
                <span>Term ({{ formatCurrency(insurancePremiums.term.coverage) }} coverage)</span>
                <span class="value">{{ formatCurrency(insurancePremiums.term.premium) }}/year</span>
              </div>
            </div>
          </div>

          <!-- Emergency Fund & Savings -->
          <div class="review-section">
            <h4><i class="pi pi-chart-line"></i> Savings & Emergency Fund</h4>
            <div class="review-items">
              <div v-if="emergencyFund" class="review-item">
                <span>Emergency Fund</span>
                <span class="value">{{ formatCurrency(emergencyFund) }}</span>
              </div>
              <div v-if="savingsInfo.monthlyLiquidSavings" class="review-item">
                <span>Monthly Liquid Savings</span>
                <span class="value">{{ formatCurrency(savingsInfo.monthlyLiquidSavings) }}</span>
              </div>
              <div v-if="savingsInfo.monthlySIP" class="review-item">
                <span>Monthly SIP</span>
                <span class="value">{{ formatCurrency(savingsInfo.monthlySIP) }}</span>
              </div>
              <div v-if="savingsInfo.weightedAvgSIPReturn" class="review-item">
                <span>Weighted Avg. SIP Return</span>
                <span class="value">{{ savingsInfo.weightedAvgSIPReturn }}%</span>
              </div>
            </div>
          </div>
        </div>

        <div v-if="error" class="error-message">
          <i class="pi pi-times-circle"></i>
          {{ error }}
        </div>
      </div>
    </div>

    <template #footer>
      <div class="wizard-footer">
        <AnButton
          v-if="currentStep > 1"
          label="Previous"
          variant="secondary"
          @click="previousStep"
          :disabled="isSubmitting"
        />
        <AnButton
          v-if="currentStep < totalSteps"
          label="Next"
          variant="primary"
          @click="nextStep"
        />
        <AnButton
          v-if="currentStep === totalSteps"
          label="Submit"
          variant="primary"
          @click="handleSubmit"
          :loading="isSubmitting"
          :disabled="isSubmitting"
        />
      </div>
    </template>
  </Dialog>
</template>

<style scoped>
/* Wizard Dialog */
.financial-wizard-dialog :deep(.p-dialog) {
  max-width: 800px;
  width: 90%;
  border-radius: 12px;
  overflow: hidden;
}

.financial-wizard-dialog :deep(.p-dialog-content) {
  padding: 18px !important;
  min-height: 400px;
  max-height: 650px;
  overflow-y: auto;
}

.financial-wizard-dialog :deep(.p-dialog-header) {
  padding: 24px 28px !important;
  border-bottom: 1px solid #e5e7eb;
}

.financial-wizard-dialog :deep(.p-dialog-footer) {
  padding: 12px !important;
}

/* Wizard Header */
.wizard-header {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 90%;
}

.wizard-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--an-text-primary);
  margin: 0;
}

.wizard-progress {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.progress-text {
  font-size: 0.9rem;
  color: var(--an-text-secondary);
  font-weight: 600;
}

.progress-bar {
  width: 100%;
  height: 6px;
  background: #e5e7eb;
  border-radius: 3px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, var(--an-primary), #10b981);
  transition: width 0.3s ease;
}

/* Wizard Content */
.wizard-content {
  padding: 24px;
  background: white;
}

.wizard-step {
  display: flex;
  flex-direction: column;
  gap: 20px;
  animation: fadeIn 0.3s ease;
  min-height: 300px;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateX(10px); }
  to { opacity: 1; transform: translateX(0); }
}

.step-title {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 1.3rem;
  font-weight: 700;
  color: var(--an-text-primary);
  margin: 0;
}

.step-title i {
  color: var(--an-primary);
  font-size: 1.5rem;
}

.step-description {
  font-size: 1rem;
  color: var(--an-text-secondary);
  margin: -10px 0 0 0;
  line-height: 1.5;
}

/* Form Grid */
.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-field label {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--an-text-primary);
}

.form-input {
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 1rem;
  transition: all 0.2s ease;
}

.form-input:focus {
  outline: none;
  border-color: var(--an-primary);
  box-shadow: 0 0 0 3px rgba(13, 124, 104, 0.1);
}

.form-input-large {
  padding: 12px 16px;
  border: 2px solid #d1d5db;
  border-radius: 8px;
  font-size: 1.2rem;
  font-weight: 600;
  text-align: center;
  transition: all 0.2s ease;
}

.form-input-large:focus {
  outline: none;
  border-color: var(--an-primary);
  box-shadow: 0 0 0 4px rgba(13, 124, 104, 0.1);
}

/* Employment Grid */
.employment-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.employment-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 24px;
  background: #f8fafc;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.employment-card:hover {
  border-color: var(--an-primary);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.employment-card.active {
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border-color: var(--an-primary);
  box-shadow: 0 4px 12px rgba(13, 124, 104, 0.2);
}

.employment-card i {
  font-size: 2rem;
  color: var(--an-primary);
}

.employment-card span {
  font-size: 1rem;
  font-weight: 600;
  color: var(--an-text-primary);
}

/* EMI Grid */
.emi-grid {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.emi-card {
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
}

.emi-card h4 {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1rem;
  font-weight: 600;
  color: var(--an-text-primary);
  margin: 0 0 12px 0;
}

.emi-card h4 i {
  color: var(--an-primary);
}

.emi-inputs {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 12px;
}

/* Insurance Grid */
.insurance-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.insurance-card {
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
}

.insurance-card h4 {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1rem;
  font-weight: 600;
  color: var(--an-text-primary);
  margin: 0 0 12px 0;
}

.insurance-card h4 i {
  color: var(--an-primary);
}

/* Emergency Fund */
.emergency-fund-input {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.emergency-fund-status {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  background: #f0fdf4;
  border: 1px solid #10b981;
  border-radius: 8px;
}

.status-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1rem;
  font-weight: 600;
  color: #047857;
}

.status-indicator i {
  font-size: 1.2rem;
}

.status-recommendation {
  margin: 0;
  font-size: 0.9rem;
  color: #065f46;
  line-height: 1.5;
}

/* Savings Grid */
.savings-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}

@media (max-width: 768px) {
  .savings-grid {
    grid-template-columns: 1fr;
  }
}

.form-field-large {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-field-large label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1rem;
  font-weight: 600;
  color: var(--an-text-primary);
}

.form-field-large label i {
  color: var(--an-primary);
}

.field-hint {
  font-size: 0.8rem;
  color: var(--an-text-secondary);
  margin: -4px 0 0 0;
  line-height: 1.4;
}

.savings-analysis {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-top: 16px;
}

.analysis-card {
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border: 1px solid #0ea5e9;
  border-radius: 8px;
  padding: 16px;
  text-align: center;
}

.analysis-card h4 {
  font-size: 0.9rem;
  font-weight: 600;
  color: #0c4a6e;
  margin: 0 0 8px 0;
}

.analysis-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #0369a1;
}

.analysis-hint {
  font-size: 0.8rem;
  color: #475569;
  margin: 4px 0 0 0;
}

/* Summary Box */
.summary-box {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border: 2px solid #0ea5e9;
  border-radius: 8px;
  margin-top: 16px;
}

.summary-label {
  font-size: 1rem;
  font-weight: 600;
  color: #0c4a6e;
}

.summary-value {
  font-size: 1.3rem;
  font-weight: 700;
  color: #0369a1;
}

/* Review Step */
.review-step {
  max-width: none;
}

.review-sections {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.review-section {
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
}

.review-section h4 {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1rem;
  font-weight: 600;
  color: var(--an-text-primary);
  margin: 0 0 12px 0;
  padding-bottom: 8px;
  border-bottom: 1px solid #e5e7eb;
}

.review-section h4 i {
  color: var(--an-primary);
}

.review-items {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.review-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  font-size: 0.9rem;
}

.review-item span {
  color: var(--an-text-secondary);
}

.review-item .value {
  font-weight: 600;
  color: var(--an-text-primary);
}

.review-item.total {
  padding-top: 12px;
  margin-top: 8px;
  border-top: 2px solid #e5e7eb;
  font-size: 1rem;
}

.review-item.total span {
  font-weight: 600;
  color: var(--an-text-primary);
}

.review-item.total .value {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--an-primary);
}

/* Error Message */
.error-message {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: #fef2f2;
  border: 1px solid #ef4444;
  border-radius: 6px;
  color: #dc2626;
  font-size: 0.9rem;
  margin-top: 16px;
}

.error-message i {
  font-size: 1rem;
}

/* Wizard Footer */
.wizard-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid #e5e7eb;
}

/* Responsive Design */
@media (max-width: 768px) {
  .form-grid,
  .employment-grid,
  .insurance-grid,
  .savings-grid,
  .savings-analysis {
    grid-template-columns: 1fr;
  }

  .emi-inputs {
    grid-template-columns: 1fr;
  }

  .wizard-title {
    font-size: 1.2rem;
  }

  .step-title {
    font-size: 1.1rem;
  }

  .wizard-content {
    padding: 16px;
    max-height: 500px;
  }
}
</style>
